import React from 'react';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { Summary } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';
import { ReactComponent as BestBfcr } from '../assets/best-bfcr-small-icon.svg';
import { ReactComponent as FastestGrowth } from '../assets/fastest-growth-small-icon.svg';
import { ReactComponent as LowestCostOfGrowth } from '../assets/lowest-cost-of-growth-small-icon.svg';
import { ReactComponent as LowestFeedPrice } from '../assets/lowest-feed-price-small-icon.svg';
import { ReactComponent as LowestCo2 } from '../assets/lowest-co2-small-icon.svg';
import { ScenarioAwardBadges } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import FormatMetricValue from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-result-tab/format-metric-value';
import { MetricValueCategory } from 'views/bluviu-nutrition/features/scenarios-v2/utils/format-metric-value';

type Props = {
  summary: Summary;
  badges: ScenarioAwardBadges;
};

type KpiRow = {
  label: string;
  value: number | null | undefined;
  valueKey?: string;
  category?: MetricValueCategory;
};

const ScenarioFeedRowBody: React.FC<Props> = ({ summary, badges }) => {
  const theme = useTheme();
  const { getScenarioPostProcessQuery: { data: postProcessData } } = useScenarioResultState();

  const currency = postProcessData?.currency;

  const leftKpis: KpiRow[] = [
    { label: 'Final weight', value: summary.finalWeightGrams, valueKey: 'finalWeightGrams' },
    { label: 'Days to reach', value: summary.daysToReach, valueKey: 'daysToReach' },
    { label: 'bFCR', value: summary.bFcr, valueKey: 'bFcr' },
    { label: 'eFCR', value: summary.eFcr, valueKey: 'eFcr' },
    { label: 'Cost of HOG gain', value: summary.feedCostOfHogGain, valueKey: 'feedCostOfHogGain' },
    {
      label: 'GWP CO₂/kg growth',
      value: summary.globalWarmingPotentialCo2KgPerKgGrowth,
      valueKey: 'globalWarmingPotentialCo2KgPerKgGrowth',
    },
    { label: 'Weight gain', value: summary.weightGainGrams, valueKey: 'weightGainGrams' },
    { label: 'Total feed cost', value: summary.costBreakdown?.feedCostTotal, category: 'currency' },
    { label: 'Total operation cost', value: summary.costBreakdown?.operationsCostTotal, category: 'currency' },
    { label: 'Total sealice cost', value: summary.costBreakdown?.treatmentCostTotal, category: 'currency' },
  ];

  const rightKpis: KpiRow[] = [
    { label: 'Feed Intake', value: summary.feedIntakeGrams, valueKey: 'feedIntakeGrams' },
    { label: 'Carcass Yield', value: summary.carcassYieldPercent, valueKey: 'carcassYieldPercent' },
    { label: 'Fillet Fat', value: summary.filletFatPercent, valueKey: 'filletFatPercent' },
    { label: 'Avg. Cost Of Growth', value: summary.feedCostOfGrowth, valueKey: 'feedCostOfGrowth' },
    { label: 'HOG Gain', value: summary.hogGainGrams, valueKey: 'hogGainGrams' },
    {
      label: 'GWP CO₂/kg feed',
      value: summary.globalWarmingPotentialCo2KgPerKgFeed,
      valueKey: 'globalWarmingPotentialCo2KgPerKgFeed',
    },
    { label: 'Potential sales value', value: summary.potentialSalesValue, valueKey: 'potentialSalesValue' },
    { label: 'Total smolt cost for all', value: summary.costBreakdown?.smoltCostTotal, category: 'currency' },
    { label: 'Total harvest cost', value: summary.costBreakdown?.harvestCostTotal, category: 'currency' },
    { label: 'Total cost', value: summary.totalCost, valueKey: 'totalCost' },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontWeight={'bold'}>Results (avg. on site):</Typography>
        <Box display="flex" flexDirection="row" flexWrap="wrap" justifyContent="center" alignItems="center" gap={2}>
          {badges.isFastestGrowth && <FastestGrowth />}
          {badges.isLowestFeedPrice && <LowestFeedPrice />}
          {badges.isLowestCo2 && <LowestCo2 />}
          {badges.isBestBFcr && <BestBfcr />}
          {badges.isLowestCostOfGrowth && <LowestCostOfGrowth />}
        </Box>
      </Box>

      <Box bgcolor={theme.palette.blue.o25} borderRadius={2} padding={5}>
        <Grid container columnSpacing={10}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box display="flex" flexDirection="column" gap={2}>
              {leftKpis.map((kpi) => (
                <Box key={kpi.label} display="flex" justifyContent="space-between" alignItems="center" gap={4}>
                  <Typography fontWeight="bold">{kpi.label}</Typography>
                  <Typography variant="body2">
                    <FormatMetricValue value={kpi.value} category={kpi.category} valueKey={kpi.valueKey} currency={currency} />
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box display="flex" flexDirection="column" gap={2}>
              {rightKpis.map((kpi) => (
                <Box key={kpi.label} display="flex" justifyContent="space-between" alignItems="center" gap={4}>
                  <Typography fontWeight="bold">{kpi.label}</Typography>
                  <Typography variant="body2">
                    <FormatMetricValue value={kpi.value} category={kpi.category} valueKey={kpi.valueKey} currency={currency} />
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default React.memo(ScenarioFeedRowBody);
